import { UndoCommand, RedoCommand } from 'vscode/vscode/vs/editor/browser/editorExtensions';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { Extensions } from 'vscode/vscode/vs/platform/configuration/common/configurationRegistry';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { IUndoRedoService } from 'vscode/vscode/vs/platform/undoRedo/common/undoRedo.service';
import { registerWorkbenchContribution2 } from 'vscode/vscode/vs/workbench/common/contributions';
import { UNDO_REDO_SOURCE } from './explorerService.js';
import { ExplorerViewletViewsContribution } from 'vscode/vscode/vs/workbench/contrib/files/browser/explorerViewlet';
import { IExplorerService } from 'vscode/vscode/vs/workbench/contrib/files/browser/files.service';
import 'vscode/vscode/vs/workbench/contrib/files/browser/files.contribution._configuration';
import 'vscode/vscode/vs/workbench/contrib/files/browser/files.contribution._editorPane';
import 'vscode/vscode/vs/workbench/contrib/files/browser/files.contribution._fileEditorFactory';

const _moduleId = "vs/workbench/contrib/files/browser/files.contribution";
registerWorkbenchContribution2(ExplorerViewletViewsContribution.ID, ExplorerViewletViewsContribution, 1 );
const configurationRegistry = ( (Registry.as(Extensions.Configuration)));
configurationRegistry.registerConfiguration({
    'id': 'explorer',
    'order': 10,
    'title': ( localizeWithPath(_moduleId, 50, "File Explorer")),
    'type': 'object',
    'properties': {
        'explorer.openEditors.visible': {
            'type': 'number',
            'description': ( localizeWithPath(
                _moduleId,
                51,
                "The initial maximum number of editors shown in the Open Editors pane. Exceeding this limit will show a scroll bar and allow resizing the pane to display more items."
            )),
            'default': 9,
            'minimum': 1
        },
        'explorer.openEditors.minVisible': {
            'type': 'number',
            'description': ( localizeWithPath(
                _moduleId,
                52,
                "The minimum number of editor slots pre-allocated in the Open Editors pane. If set to 0 the Open Editors pane will dynamically resize based on the number of editors."
            )),
            'default': 0,
            'minimum': 0
        },
        'explorer.openEditors.sortOrder': {
            'type': 'string',
            'enum': ['editorOrder', 'alphabetical', 'fullPath'],
            'description': ( localizeWithPath(
                _moduleId,
                53,
                "Controls the sorting order of editors in the Open Editors pane."
            )),
            'enumDescriptions': [
                ( localizeWithPath(
                    _moduleId,
                    54,
                    'Editors are ordered in the same order editor tabs are shown.'
                )),
                ( localizeWithPath(
                    _moduleId,
                    55,
                    'Editors are ordered alphabetically by tab name inside each editor group.'
                )),
                ( localizeWithPath(
                    _moduleId,
                    56,
                    'Editors are ordered alphabetically by full path inside each editor group.'
                ))
            ],
            'default': 'editorOrder'
        },
        'explorer.autoReveal': {
            'type': ['boolean', 'string'],
            'enum': [true, false, 'focusNoScroll'],
            'default': true,
            'enumDescriptions': [
                ( localizeWithPath(_moduleId, 57, 'Files will be revealed and selected.')),
                ( localizeWithPath(_moduleId, 58, 'Files will not be revealed and selected.')),
                ( localizeWithPath(
                    _moduleId,
                    59,
                    'Files will not be scrolled into view, but will still be focused.'
                )),
            ],
            'description': ( localizeWithPath(
                _moduleId,
                60,
                "Controls whether the Explorer should automatically reveal and select files when opening them."
            ))
        },
        'explorer.autoRevealExclude': {
            'type': 'object',
            'markdownDescription': ( localizeWithPath(
                _moduleId,
                61,
                "Configure paths or [glob patterns](https://aka.ms/vscode-glob-patterns) for excluding files and folders from being revealed and selected in the Explorer when they are opened. Glob patterns are always evaluated relative to the path of the workspace folder unless they are absolute paths."
            )),
            'default': { '**/node_modules': true, '**/bower_components': true },
            'additionalProperties': {
                'anyOf': [
                    {
                        'type': 'boolean',
                        'description': ( localizeWithPath(
                            _moduleId,
                            62,
                            "The glob pattern to match file paths against. Set to true or false to enable or disable the pattern."
                        )),
                    },
                    {
                        type: 'object',
                        properties: {
                            when: {
                                type: 'string',
                                pattern: '\\w*\\$\\(basename\\)\\w*',
                                default: '$(basename).ext',
                                description: ( localizeWithPath(
                                    _moduleId,
                                    63,
                                    'Additional check on the siblings of a matching file. Use $(basename) as variable for the matching file name.'
                                ))
                            }
                        }
                    }
                ]
            }
        },
        'explorer.enableDragAndDrop': {
            'type': 'boolean',
            'description': ( localizeWithPath(
                _moduleId,
                64,
                "Controls whether the Explorer should allow to move files and folders via drag and drop. This setting only effects drag and drop from inside the Explorer."
            )),
            'default': true
        },
        'explorer.confirmDragAndDrop': {
            'type': 'boolean',
            'description': ( localizeWithPath(
                _moduleId,
                65,
                "Controls whether the Explorer should ask for confirmation to move files and folders via drag and drop."
            )),
            'default': true
        },
        'explorer.confirmPasteNative': {
            'type': 'boolean',
            'description': ( localizeWithPath(
                _moduleId,
                66,
                "Controls whether the Explorer should ask for confirmation when pasting native files and folders."
            )),
            'default': true
        },
        'explorer.confirmDelete': {
            'type': 'boolean',
            'description': ( localizeWithPath(
                _moduleId,
                67,
                "Controls whether the Explorer should ask for confirmation when deleting a file via the trash."
            )),
            'default': true
        },
        'explorer.enableUndo': {
            'type': 'boolean',
            'description': ( localizeWithPath(
                _moduleId,
                68,
                "Controls whether the Explorer should support undoing file and folder operations."
            )),
            'default': true
        },
        'explorer.confirmUndo': {
            'type': 'string',
            'enum': ["verbose" , "default" , "light" ],
            'description': ( localizeWithPath(
                _moduleId,
                69,
                "Controls whether the Explorer should ask for confirmation when undoing."
            )),
            'default': "default" ,
            'enumDescriptions': [
                ( localizeWithPath(_moduleId, 70, 'Explorer will prompt before all undo operations.')),
                ( localizeWithPath(_moduleId, 71, 'Explorer will prompt before destructive undo operations.')),
                ( localizeWithPath(
                    _moduleId,
                    72,
                    'Explorer will not prompt before undo operations when focused.'
                )),
            ],
        },
        'explorer.expandSingleFolderWorkspaces': {
            'type': 'boolean',
            'description': ( localizeWithPath(
                _moduleId,
                73,
                "Controls whether the Explorer should expand multi-root workspaces containing only one folder during initialization"
            )),
            'default': true
        },
        'explorer.sortOrder': {
            'type': 'string',
            'enum': ["default" , "mixed" , "filesFirst" , "type" , "modified" , "foldersNestsFiles" ],
            'default': "default" ,
            'enumDescriptions': [
                ( localizeWithPath(
                    _moduleId,
                    74,
                    'Files and folders are sorted by their names. Folders are displayed before files.'
                )),
                ( localizeWithPath(
                    _moduleId,
                    75,
                    'Files and folders are sorted by their names. Files are interwoven with folders.'
                )),
                ( localizeWithPath(
                    _moduleId,
                    76,
                    'Files and folders are sorted by their names. Files are displayed before folders.'
                )),
                ( localizeWithPath(
                    _moduleId,
                    77,
                    'Files and folders are grouped by extension type then sorted by their names. Folders are displayed before files.'
                )),
                ( localizeWithPath(
                    _moduleId,
                    78,
                    'Files and folders are sorted by last modified date in descending order. Folders are displayed before files.'
                )),
                ( localizeWithPath(
                    _moduleId,
                    79,
                    'Files and folders are sorted by their names. Folders are displayed before files. Files with nested children are displayed before other files.'
                ))
            ],
            'markdownDescription': ( localizeWithPath(
                _moduleId,
                80,
                "Controls the property-based sorting of files and folders in the Explorer. When `#explorer.fileNesting.enabled#` is enabled, also controls sorting of nested files."
            ))
        },
        'explorer.sortOrderLexicographicOptions': {
            'type': 'string',
            'enum': ["default" , "upper" , "lower" , "unicode" ],
            'default': "default" ,
            'enumDescriptions': [
                ( localizeWithPath(_moduleId, 81, 'Uppercase and lowercase names are mixed together.')),
                ( localizeWithPath(
                    _moduleId,
                    82,
                    'Uppercase names are grouped together before lowercase names.'
                )),
                ( localizeWithPath(
                    _moduleId,
                    83,
                    'Lowercase names are grouped together before uppercase names.'
                )),
                ( localizeWithPath(_moduleId, 84, 'Names are sorted in Unicode order.'))
            ],
            'description': ( localizeWithPath(
                _moduleId,
                85,
                "Controls the lexicographic sorting of file and folder names in the Explorer."
            ))
        },
        'explorer.decorations.colors': {
            type: 'boolean',
            description: ( localizeWithPath(_moduleId, 86, "Controls whether file decorations should use colors.")),
            default: true
        },
        'explorer.decorations.badges': {
            type: 'boolean',
            description: ( localizeWithPath(_moduleId, 87, "Controls whether file decorations should use badges.")),
            default: true
        },
        'explorer.incrementalNaming': {
            'type': 'string',
            enum: ['simple', 'smart', 'disabled'],
            enumDescriptions: [
                ( localizeWithPath(
                    _moduleId,
                    88,
                    "Appends the word \"copy\" at the end of the duplicated name potentially followed by a number."
                )),
                ( localizeWithPath(
                    _moduleId,
                    89,
                    "Adds a number at the end of the duplicated name. If some number is already part of the name, tries to increase that number."
                )),
                ( localizeWithPath(
                    _moduleId,
                    90,
                    "Disables incremental naming. If two files with the same name exist you will be prompted to overwrite the existing file."
                ))
            ],
            description: ( localizeWithPath(
                _moduleId,
                91,
                "Controls which naming strategy to use when giving a new name to a duplicated Explorer item on paste."
            )),
            default: 'simple'
        },
        'explorer.compactFolders': {
            'type': 'boolean',
            'description': ( localizeWithPath(
                _moduleId,
                92,
                "Controls whether the Explorer should render folders in a compact form. In such a form, single child folders will be compressed in a combined tree element. Useful for Java package structures, for example."
            )),
            'default': true
        },
        'explorer.copyRelativePathSeparator': {
            'type': 'string',
            'enum': [
                '/',
                '\\',
                'auto'
            ],
            'enumDescriptions': [
                ( localizeWithPath(_moduleId, 93, "Use slash as path separation character.")),
                ( localizeWithPath(_moduleId, 94, "Use backslash as path separation character.")),
                ( localizeWithPath(_moduleId, 95, "Uses operating system specific path separation character.")),
            ],
            'description': ( localizeWithPath(
                _moduleId,
                96,
                "The path separation character used when copying relative file paths."
            )),
            'default': 'auto'
        },
        'explorer.excludeGitIgnore': {
            type: 'boolean',
            markdownDescription: ( localizeWithPath(
                _moduleId,
                97,
                "Controls whether entries in .gitignore should be parsed and excluded from the Explorer. Similar to {0}.",
                '`#files.exclude#`'
            )),
            default: false,
            scope: 4
        },
        'explorer.fileNesting.enabled': {
            'type': 'boolean',
            scope: 4 ,
            'markdownDescription': ( localizeWithPath(
                _moduleId,
                98,
                "Controls whether file nesting is enabled in the Explorer. File nesting allows for related files in a directory to be visually grouped together under a single parent file."
            )),
            'default': false,
        },
        'explorer.fileNesting.expand': {
            'type': 'boolean',
            'markdownDescription': ( localizeWithPath(
                _moduleId,
                99,
                "Controls whether file nests are automatically expanded. {0} must be set for this to take effect.",
                '`#explorer.fileNesting.enabled#`'
            )),
            'default': true,
        },
        'explorer.fileNesting.patterns': {
            'type': 'object',
            scope: 4 ,
            'markdownDescription': ( localizeWithPath(
                _moduleId,
                100,
                "Controls nesting of files in the Explorer. {0} must be set for this to take effect. Each __Item__ represents a parent pattern and may contain a single `*` character that matches any string. Each __Value__ represents a comma separated list of the child patterns that should be shown nested under a given parent. Child patterns may contain several special tokens:\n- `${capture}`: Matches the resolved value of the `*` from the parent pattern\n- `${basename}`: Matches the parent file's basename, the `file` in `file.ts`\n- `${extname}`: Matches the parent file's extension, the `ts` in `file.ts`\n- `${dirname}`: Matches the parent file's directory name, the `src` in `src/file.ts`\n- `*`:  Matches any string, may only be used once per child pattern",
                '`#explorer.fileNesting.enabled#`'
            )),
            patternProperties: {
                '^[^*]*\\*?[^*]*$': {
                    markdownDescription: ( localizeWithPath(
                        _moduleId,
                        101,
                        "Each key pattern may contain a single `*` character which will match any string."
                    )),
                    type: 'string',
                    pattern: '^([^,*]*\\*?[^,*]*)(, ?[^,*]*\\*?[^,*]*)*$',
                }
            },
            additionalProperties: false,
            'default': {
                '*.ts': '${capture}.js',
                '*.js': '${capture}.js.map, ${capture}.min.js, ${capture}.d.ts',
                '*.jsx': '${capture}.js',
                '*.tsx': '${capture}.ts',
                'tsconfig.json': 'tsconfig.*.json',
                'package.json': 'package-lock.json, yarn.lock, pnpm-lock.yaml, bun.lockb',
            }
        }
    }
});
UndoCommand.addImplementation(110, 'explorer', (accessor) => {
    const undoRedoService = accessor.get(IUndoRedoService);
    const explorerService = accessor.get(IExplorerService);
    const configurationService = accessor.get(IConfigurationService);
    const explorerCanUndo = configurationService.getValue().explorer.enableUndo;
    if (explorerService.hasViewFocus() && undoRedoService.canUndo(UNDO_REDO_SOURCE) && explorerCanUndo) {
        undoRedoService.undo(UNDO_REDO_SOURCE);
        return true;
    }
    return false;
});
RedoCommand.addImplementation(110, 'explorer', (accessor) => {
    const undoRedoService = accessor.get(IUndoRedoService);
    const explorerService = accessor.get(IExplorerService);
    const configurationService = accessor.get(IConfigurationService);
    const explorerCanUndo = configurationService.getValue().explorer.enableUndo;
    if (explorerService.hasViewFocus() && undoRedoService.canRedo(UNDO_REDO_SOURCE) && explorerCanUndo) {
        undoRedoService.redo(UNDO_REDO_SOURCE);
        return true;
    }
    return false;
});
